import React, { useState } from 'react';
import { Users, Plus, Pencil, Trash2, History, X } from 'lucide-react';
import { useLocation } from '../lib/location';
import { useToast } from '../lib/toast';
import {
  useReservations,
  useCreateReservation,
  usePatchReservation,
  useDeleteReservation,
  useReservationAuditLog,
} from '../hooks/useReservations';
import type { Reservation, ReservationStatus } from '../api/types';
import '../styles/reservations.css';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatDateTimeShort(iso: string): string {
  return new Date(iso).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function toDatetimeLocalValue(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromDatetimeLocal(value: string): string {
  return new Date(value).toISOString();
}

const STATUS_LABEL: Record<ReservationStatus, string> = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  cancelled: 'Cancelled',
};

// ---------------------------------------------------------------------------
// Form modal
// ---------------------------------------------------------------------------

interface FormState {
  name: string;
  partySize: string;
  reservedAt: string;
  phone: string;
  specialRequests: string;
  status: ReservationStatus;
}

function blankForm(): FormState {
  const d = new Date();
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + 1);
  return {
    name: '',
    partySize: '2',
    reservedAt: toDatetimeLocalValue(d.toISOString()),
    phone: '',
    specialRequests: '',
    status: 'pending',
  };
}

function formFromReservation(r: Reservation): FormState {
  return {
    name: r.name,
    partySize: String(r.partySize),
    reservedAt: toDatetimeLocalValue(r.reservedAt),
    phone: r.phone ?? '',
    specialRequests: r.specialRequests ?? '',
    status: r.status,
  };
}

interface ReservationFormModalProps {
  title: string;
  initial: FormState;
  saving: boolean;
  allowCancelToggle?: boolean;
  onSave: (f: FormState) => void;
  onClose: () => void;
}

function ReservationFormModal({
  title,
  initial,
  saving,
  allowCancelToggle,
  onSave,
  onClose,
}: ReservationFormModalProps): React.ReactElement {
  const [form, setForm] = useState<FormState>(initial);

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSave(form);
  }

  return (
    <div className="res-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="res-modal" role="dialog" aria-modal="true" aria-label={title}>
        <h2>{title}</h2>

        <form className="res-modal-fields" onSubmit={handleSubmit}>
          <div className="res-field">
            <label htmlFor="res-name">Name</label>
            <input
              id="res-name"
              type="text"
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="Guest name"
              required
              maxLength={200}
              autoFocus
            />
          </div>

          <div className="res-field">
            <label htmlFor="res-party">Party size</label>
            <input
              id="res-party"
              type="number"
              value={form.partySize}
              onChange={(e) => set('partySize', e.target.value)}
              min={1}
              max={999}
              required
            />
          </div>

          <div className="res-field">
            <label htmlFor="res-phone">Phone <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span></label>
            <input
              id="res-phone"
              type="tel"
              value={form.phone}
              onChange={(e) => set('phone', e.target.value)}
              placeholder="e.g. 555-867-5309"
              maxLength={30}
            />
          </div>

          <div className="res-field">
            <label htmlFor="res-date">Date &amp; time</label>
            <input
              id="res-date"
              type="datetime-local"
              value={form.reservedAt}
              onChange={(e) => set('reservedAt', e.target.value)}
              required
            />
          </div>

          {allowCancelToggle && (
            <div className="res-field res-field--inline">
              <input
                id="res-cancelled"
                type="checkbox"
                checked={form.status === 'cancelled'}
                onChange={(e) => set('status', e.target.checked ? 'cancelled' : 'confirmed')}
              />
              <label htmlFor="res-cancelled">Mark as cancelled</label>
            </div>
          )}

          <div className="res-field">
            <label htmlFor="res-special">Special requests</label>
            <textarea
              id="res-special"
              value={form.specialRequests}
              onChange={(e) => set('specialRequests', e.target.value)}
              placeholder="Dietary restrictions, accessibility needs, occasion…"
              rows={3}
            />
          </div>

          <div className="res-modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving || !form.name.trim() || !form.reservedAt}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Audit log panel (inline below selected row)
// ---------------------------------------------------------------------------

function AuditLogPanel({ reservationId, onClose }: { reservationId: string; onClose: () => void }): React.ReactElement {
  const { data, isLoading } = useReservationAuditLog(reservationId);

  return (
    <div className="res-audit-panel">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3>Audit log</h3>
        <button className="icon-btn" onClick={onClose} aria-label="Close audit log">
          <X size={14} />
        </button>
      </div>
      {isLoading ? (
        <div className="text-muted" style={{ fontSize: 'var(--font-xs)' }}>Loading…</div>
      ) : !data?.items.length ? (
        <div className="text-muted" style={{ fontSize: 'var(--font-xs)' }}>No audit entries found.</div>
      ) : (
        data.items.map((entry) => (
          <div key={entry.id} className="res-audit-entry">
            <span className={`res-audit-action res-audit-action--${entry.action}`}>{entry.action}</span>
            <span className="res-audit-meta">
              {formatDateTimeShort(entry.changedAt)}
            </span>
          </div>
        ))
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Delete confirm modal
// ---------------------------------------------------------------------------

function DeleteConfirmModal({
  reservation,
  deleting,
  onConfirm,
  onClose,
}: {
  reservation: Reservation;
  deleting: boolean;
  onConfirm: () => void;
  onClose: () => void;
}): React.ReactElement {
  return (
    <div className="res-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="res-modal" role="dialog" aria-modal="true" aria-label="Delete reservation">
        <h2>Delete reservation?</h2>
        <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 'var(--font-sm)' }}>
          This will permanently delete the reservation for <strong>{reservation.name}</strong> on{' '}
          {formatDateTime(reservation.reservedAt)}. This action cannot be undone, but the audit log is preserved.
        </p>
        <div className="res-modal-footer">
          <button className="btn btn-ghost" onClick={onClose} disabled={deleting}>
            Cancel
          </button>
          <button className="btn btn-danger" onClick={onConfirm} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

type StatusFilter = 'all' | ReservationStatus;

export function ReservationsPage(): React.ReactElement {
  const { locationId } = useLocation();
  const { toast } = useToast();

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Reservation | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Reservation | null>(null);
  const [auditTarget, setAuditTarget] = useState<string | null>(null);

  const queryParams = {
    ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
    ...(fromDate ? { from: new Date(fromDate).toISOString() } : {}),
    ...(toDate ? {
      to: (() => { const d = new Date(toDate); d.setHours(23, 59, 59, 999); return d.toISOString(); })(),
    } : {}),
    limit: 200,
  };

  const { data, isLoading } = useReservations(locationId, queryParams);
  const items = data?.items ?? [];

  const createMutation = useCreateReservation(locationId ?? '');
  const patchMutation = usePatchReservation();
  const deleteMutation = useDeleteReservation();

  function handleCreate(form: FormState) {
    createMutation.mutate(
      {
        name: form.name.trim(),
        partySize: Number(form.partySize),
        reservedAt: fromDatetimeLocal(form.reservedAt),
        phone: form.phone.trim() || null,
        specialRequests: form.specialRequests.trim() || null,
        status: 'confirmed',
      },
      {
        onSuccess: () => {
          setCreateOpen(false);
          toast.success('Reservation created');
        },
        onError: () => toast.error('Failed to create reservation'),
      },
    );
  }

  function handleEdit(form: FormState) {
    if (!editTarget) return;
    patchMutation.mutate(
      {
        id: editTarget.id,
        body: {
          name: form.name.trim(),
          partySize: Number(form.partySize),
          reservedAt: fromDatetimeLocal(form.reservedAt),
          phone: form.phone.trim() || null,
          specialRequests: form.specialRequests.trim() || null,
          status: form.status,
        },
      },
      {
        onSuccess: () => {
          setEditTarget(null);
          toast.success('Reservation updated');
        },
        onError: () => toast.error('Failed to update reservation'),
      },
    );
  }

  function handleDelete() {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id, {
      onSuccess: () => {
        setDeleteTarget(null);
        toast.success('Reservation deleted');
      },
      onError: () => toast.error('Failed to delete reservation'),
    });
  }

  const STATUS_TABS: { key: StatusFilter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'pending', label: 'Pending' },
    { key: 'confirmed', label: 'Confirmed' },
    { key: 'cancelled', label: 'Cancelled' },
  ];

  return (
    <div className="canto-page canto-page-narrow reservations-page">
      {/* Page header */}
      <div className="reservations-page-header">
        <div className="canto-page-header">
          <h1>Reservations</h1>
        </div>
        <button className="btn btn-primary" onClick={() => setCreateOpen(true)}>
          <Plus size={16} /> New reservation
        </button>
      </div>

      {/* Filters */}
      <div className="reservations-filter-bar">
        <div className="reservations-status-tabs" role="tablist">
          {STATUS_TABS.map(({ key, label }) => (
            <button
              key={key}
              role="tab"
              className={`reservations-status-tab${statusFilter === key ? ' reservations-status-tab-active' : ''}`}
              onClick={() => setStatusFilter(key)}
            >
              {label}
            </button>
          ))}
        </div>

        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          title="From date"
          aria-label="From date"
        />
        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          title="To date"
          aria-label="To date"
        />
        {(fromDate || toDate) && (
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => { setFromDate(''); setToDate(''); }}
          >
            Clear dates
          </button>
        )}
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="text-muted" style={{ padding: 'var(--space-4) 0' }}>Loading…</div>
      ) : items.length === 0 ? (
        <div className="reservations-empty">
          <p>No reservations found.</p>
        </div>
      ) : (
        <div className="reservations-table-wrap">
          <table className="reservations-table">
            <thead>
              <tr>
                <th>Date &amp; Time</th>
                <th>Name</th>
                <th>Phone</th>
                <th>Party</th>
                <th>Status</th>
                <th>Special requests</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <React.Fragment key={r.id}>
                  <tr>
                    <td className="mono">{formatDateTime(r.reservedAt)}</td>
                    <td style={{ fontWeight: 600 }}>{r.name}</td>
                    <td style={{ color: 'var(--text-muted)' }}>{r.phone ?? '—'}</td>
                    <td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <Users size={13} />
                        {r.partySize}
                      </span>
                    </td>
                    <td>
                      <span className={`res-status-badge res-status-badge--${r.status}`}>
                        {STATUS_LABEL[r.status]}
                      </span>
                    </td>
                    <td style={{ color: 'var(--text-muted)', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.specialRequests ?? '—'}
                    </td>
                    <td>
                      <div className="res-row-actions">
                        <button
                          className="icon-btn"
                          title="Audit log"
                          aria-label="View audit log"
                          onClick={() => setAuditTarget(auditTarget === r.id ? null : r.id)}
                        >
                          <History size={15} />
                        </button>
                        <button
                          className="icon-btn"
                          title="Edit"
                          aria-label="Edit reservation"
                          onClick={() => setEditTarget(r)}
                        >
                          <Pencil size={15} />
                        </button>
                        <button
                          className="icon-btn"
                          title="Delete"
                          aria-label="Delete reservation"
                          onClick={() => setDeleteTarget(r)}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {auditTarget === r.id && (
                    <tr>
                      <td colSpan={7} style={{ padding: 'var(--space-3) var(--space-3) var(--space-3) calc(var(--space-3) * 2)', background: 'var(--bg-subtle)' }}>
                        <AuditLogPanel reservationId={r.id} onClose={() => setAuditTarget(null)} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      {createOpen && (
        <ReservationFormModal
          title="New reservation"
          initial={blankForm()}
          saving={createMutation.isPending}
          onSave={handleCreate}
          onClose={() => setCreateOpen(false)}
        />
      )}

      {editTarget && (
        <ReservationFormModal
          title="Edit reservation"
          initial={formFromReservation(editTarget)}
          saving={patchMutation.isPending}
          allowCancelToggle
          onSave={handleEdit}
          onClose={() => setEditTarget(null)}
        />
      )}

      {deleteTarget && (
        <DeleteConfirmModal
          reservation={deleteTarget}
          deleting={deleteMutation.isPending}
          onConfirm={handleDelete}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
